import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Avatar,
  TextField, MenuItem, Button, IconButton, Tooltip, CircularProgress,
  TablePagination, Collapse, Stack, Divider, InputAdornment, Alert
} from '@mui/material';
import {
  Search, Refresh, ExpandMore, ExpandLess,
  Login as LoginIcon, Logout as LogoutIcon,
  AddCircle as CreateIcon, Edit as UpdateIcon,
  Delete as DeleteIcon, Key as PasswordIcon,
  Assignment as AuditIcon, PersonOff as ToggleIcon,
  BarChart as StatsIcon, ImportExport as ImportIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { auditAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Event type config ─────────────────────────────────────────────────────────
const EVENT_META = {
  login:           { label: 'Login',           color: 'success', icon: <LoginIcon sx={{ fontSize: 14 }} /> },
  logout:          { label: 'Logout',          color: 'warning', icon: <LogoutIcon sx={{ fontSize: 14 }} /> },
  create:          { label: 'Created',         color: 'success', icon: <CreateIcon sx={{ fontSize: 14 }} /> },
  update:          { label: 'Updated',         color: 'info',    icon: <UpdateIcon sx={{ fontSize: 14 }} /> },
  delete:          { label: 'Deleted',         color: 'error',   icon: <DeleteIcon sx={{ fontSize: 14 }} /> },
  password_change: { label: 'Pwd Changed',     color: 'warning', icon: <PasswordIcon sx={{ fontSize: 14 }} /> },
  account_toggle:  { label: 'Acct Toggle',     color: 'warning', icon: <ToggleIcon sx={{ fontSize: 14 }} /> },
  import:          { label: 'Import',          color: 'secondary', icon: <ImportIcon sx={{ fontSize: 14 }} /> },
  export:          { label: 'Export',          color: 'secondary', icon: <ImportIcon sx={{ fontSize: 14 }} /> },
  view:            { label: 'Viewed',          color: 'default', icon: <AuditIcon sx={{ fontSize: 14 }} /> },
};

const MODULE_COLORS = {
  students: '#1565c0', teachers: '#2e7d32', subjects: '#6a1b9a',
  assessments: '#e65100', marks: '#00838f', enrolments: '#f57c00',
  auth: '#37474f', class_assignments: '#1565c0', settings: '#795548',
};

const formatDT = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const EventChip = ({ type }) => {
  const m = EVENT_META[type] || { label: type, color: 'default', icon: null };
  return (
    <Chip
      icon={m.icon}
      label={m.label}
      size="small"
      color={m.color}
      variant="outlined"
      sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
    />
  );
};

const ModuleChip = ({ module }) => (
  <Chip
    label={module}
    size="small"
    sx={{
      fontSize: 11, height: 20, fontWeight: 600,
      bgcolor: (MODULE_COLORS[module] || '#607d8b') + '18',
      color: MODULE_COLORS[module] || '#607d8b',
      border: `1px solid ${(MODULE_COLORS[module] || '#607d8b')}44`
    }}
  />
);

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color = '#1565c0', icon }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: color + '18', width: 40, height: 40, color }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} color={color}>{value ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────────
export default function AuditLogs() {
  const { enqueueSnackbar } = useSnackbar();
  const { loading: authLoading } = useAuth();

  const [logs, setLogs]       = useState([]);
  const [stats, setStats]     = useState(null);
  const [filterOpts, setFilterOpts] = useState({ users: [], modules: [] });
  const [loading, setLoading] = useState(false);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedId, setExpandedId]   = useState(null);

  const [filters, setFilters] = useState({
    search: '', user_id: '', event_type: '', module: '',
    date_from: '', date_to: ''
  });

  const EVENT_TYPES = Object.keys(EVENT_META);

  // Load filter options + stats once auth is ready
  useEffect(() => {
    if (authLoading) return;
    auditAPI.getFilters().then(r => { if (r.data.success) setFilterOpts(r.data); }).catch(() => {});
    auditAPI.getStats().then(r => { if (r.data.success) setStats(r.data.data); }).catch(() => {});
  }, [authLoading]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditAPI.getLogs({
        ...filters,
        page: page + 1,
        limit: rowsPerPage
      });
      if (res.data.success) {
        setLogs(res.data.data);
        setTotal(res.data.total);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load audit logs', { variant: 'error' });
    } finally { setLoading(false); }
  }, [filters, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { if (!authLoading) loadLogs(); }, [loadLogs, authLoading]);

  const handleFilterChange = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ search: '', user_id: '', event_type: '', module: '', date_from: '', date_to: '' });
    setPage(0);
  };

  const isCritical = (log) => ['delete', 'account_toggle', 'password_change'].includes(log.event_type);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AuditIcon color="primary" /> Audit Trail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete activity log — every create, update, delete and login across the system
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={loadLogs} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Stats row */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard label="Today's Activity" value={stats.today} color="#1565c0" icon={<AuditIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="This Week" value={stats.this_week} color="#2e7d32" icon={<StatsIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Total Logs" value={stats.total?.toLocaleString()} color="#6a1b9a" icon={<AuditIcon />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Active Users (30d)" value={stats.top_users?.length} color="#e65100" icon={<AuditIcon />} />
          </Grid>
        </Grid>
      )}

      {/* Event type summary pills */}
      {stats?.by_type?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {stats.by_type.map(t => (
              <Chip
                key={t.event_type}
                label={`${EVENT_META[t.event_type]?.label || t.event_type}: ${t.total}`}
                size="small"
                color={EVENT_META[t.event_type]?.color || 'default'}
                variant="outlined"
                onClick={() => handleFilterChange('event_type', t.event_type)}
                sx={{ cursor: 'pointer', fontSize: 11 }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth placeholder="Search actions, records…" value={filters.search}
              onChange={e => handleFilterChange('search', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label="User" value={filters.user_id}
              onChange={e => handleFilterChange('user_id', e.target.value)}>
              <MenuItem value="">All Users</MenuItem>
              {filterOpts.users.map(u => (
                <MenuItem key={u.user_id} value={u.user_id}>{u.full_name || u.username}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label="Event" value={filters.event_type}
              onChange={e => handleFilterChange('event_type', e.target.value)}>
              <MenuItem value="">All Events</MenuItem>
              {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{EVENT_META[t].label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label="Module" value={filters.module}
              onChange={e => handleFilterChange('module', e.target.value)}>
              <MenuItem value="">All Modules</MenuItem>
              {filterOpts.modules.map(m => (
                <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>
                  {m.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField size="small" fullWidth label="From" type="date" value={filters.date_from}
              onChange={e => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField size="small" fullWidth label="To" type="date" value={filters.date_to}
              onChange={e => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={0.5}>
            <Tooltip title="Clear filters">
              <Button size="small" onClick={clearFilters} variant="outlined" sx={{ minWidth: 0, px: 1 }}>✕</Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                {['Timestamp','User','Event','Module','Action / Description','IP Address',''].map(h => (
                  <TableCell key={h} sx={{ color: 'rgba(255,255,255,.85)', fontWeight: 700, fontSize: 11, py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : logs.map((log, idx) => (
                <React.Fragment key={log.id}>
                  <TableRow
                    hover
                    sx={{
                      bgcolor: isCritical(log) ? '#fff2f0' : idx % 2 === 0 ? 'white' : '#fafafa',
                      borderLeft: isCritical(log) ? '3px solid #ff4d4f' : '3px solid transparent',
                    }}
                  >
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap', py: 1 }}>
                      {formatDT(log.created_at)}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: 'primary.main' }}>
                          {(log.full_name || log.username || '?')[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} fontSize={12} lineHeight={1.2}>
                            {log.full_name || log.username || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontSize={10}>
                            {log.role}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <EventChip type={log.event_type} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <ModuleChip module={log.module} />
                    </TableCell>
                    <TableCell sx={{ py: 1, maxWidth: 320 }}>
                      <Typography variant="body2" fontSize={13}>{log.action}</Typography>
                      {log.record_label && (
                        <Typography variant="caption" color="text.secondary">{log.record_label}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
                      {log.ip_address || '—'}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <Tooltip title={expandedId === log.id ? 'Hide details' : 'View details'}>
                          <IconButton size="small" onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                            {expandedId === log.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Expanded details row */}
                  {expandedId === log.id && log.details && (
                    <TableRow sx={{ bgcolor: '#f0f7ff' }}>
                      <TableCell colSpan={7} sx={{ py: 1.5, px: 3 }}>
                        <Typography variant="caption" fontWeight={700} color="primary" sx={{ display: 'block', mb: 1 }}>
                          Event Details
                        </Typography>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: 1.5,
                          bgcolor: 'white', border: '1px solid #e3e8f0',
                          borderRadius: 1.5, p: 1.5
                        }}>
                          {Object.entries(log.details).map(([k, v]) => (
                            <Box key={k}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                                {k.replace(/_/g, ' ')}
                              </Typography>
                              <Typography variant="body2" fontSize={12} sx={{ wordBreak: 'break-all' }}>
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
}
